import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { EncounterTemplateDto } from '../lib/api/modules/specialty';

/** Templates are only seeded for these visit types today; other departments/specialties fall back to NEW. */
const VISIT_TYPE_BY_TEMPLATE_KEY: Record<string, string> = {
  GYNECOLOGY_OB: 'ANTENATAL',
  PEDIATRICS: 'WELL_CHILD',
};

/**
 * Resolves the specialty encounter template for the doctor currently in context.
 *
 * Most patient-window entry points in this app (queue table, search, notification bell) open
 * `PatientProfile` with no `appointment` at all, so relying solely on `appointment.doctorId` misses
 * the majority of real navigation paths. This hook prefers `appointmentDoctorId` when given, and
 * falls back to the logged-in user's own doctor profile — the doctor viewing the window is almost
 * always the one whose department/specialty should drive the extra fields.
 */
export function useSpecialtyTemplate(appointmentDoctorId?: string) {
  const { userData } = useAuth();
  const [template, setTemplate] = useState<EncounterTemplateDto | null>(null);
  const [templateKey, setTemplateKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      try {
        const [doctorsRes, deptsRes] = await Promise.all([
          api.doctors.getAll(),
          api.departments.list(),
        ]);
        const doctors = doctorsRes.data || [];
        const doctor =
          (appointmentDoctorId && doctors.find((d) => d.id === appointmentDoctorId)) ||
          (userData?.role === 'doctor' ? doctors.find((d) => d.userId === userData.id) : undefined);

        const department = doctor?.departmentId
          ? deptsRes.data?.find((d) => d.id === doctor.departmentId)
          : undefined;
        const key = department?.templateKey || null;
        const visitType = key ? VISIT_TYPE_BY_TEMPLATE_KEY[key] : undefined;

        if (!key || !visitType) {
          if (!cancelled) { setTemplate(null); setTemplateKey(null); }
          return;
        }

        const templateRes = await api.encounterTemplates.resolve(department?.id, visitType);
        if (!cancelled) {
          setTemplate(templateRes.data || null);
          setTemplateKey(key);
        }
      } catch {
        if (!cancelled) { setTemplate(null); setTemplateKey(null); }
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [appointmentDoctorId, userData?.id, userData?.role]);

  return { template, templateKey };
}
